'use client';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { clsx } from '@sumup-oss/circuit-ui';
import Cookies from 'js-cookie';

import styles from './ChatIframe.module.css';
import { ChatIframeMessageEventDataType } from './ChatIframe.types';

import { ChatProviderContext } from 'src/providers/Chat/Chat';
import {
  CONSENT_CATEGORIES,
  useCookieConsent,
} from 'src/components/CookieConsent/public';

function invert<Key extends string, Value extends string>(
  object: Record<Key, Value>,
): Record<Value, Key> {
  return Object.entries(object).reduce(
    (acc, [key, value]: [Key, Value]) => {
      acc[value] = key;
      return acc;
    },
    {} as Record<Value, Key>,
  );
}

const ONE_TRUST_CATEGORIES = invert(CONSENT_CATEGORIES);

const ChatIframe = ({
  locale,
  origin,
}: {
  locale: string;
  origin?: string;
}) => {
  // Context
  const {
    dispatch,
    state: { isMinimized, conversationId, iFrameCounter },
  } = useContext(ChatProviderContext);

  // Configurations
  const iframeSrc = new URL(process.env.NEXT_PUBLIC_CHAT_URL);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // Post Message Logic (Send)
  const sendMessage = (payload: unknown) => {
    iframeRef.current?.contentWindow?.postMessage(payload, iframeSrc.href);
  };

  // Resize Logic
  const [size, setSize] = useState({ width: '700px', height: '720px' });
  const onResizeData = (width: string | number, height: string | number) => {
    setSize({
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
    });
  };

  const onClose = (eventData: ChatIframeMessageEventDataType) => {
    dispatch({ type: 'SET_ACTIVE', payload: eventData.restart ?? false });
    dispatch({ type: 'SET_MINIMIZED', payload: false });
    dispatch({ type: 'SET_CONVERSATION_ID', payload: '' });
    if (eventData.restart) {
      dispatch({ type: 'SET_RESTART_CHAT' });
    }
  };

  // Minimize logic
  const isAIAssistantMinimized = false;
  const onMinimize = (minimizedStatus: boolean) => {
    dispatch({ type: 'SET_MINIMIZED', payload: minimizedStatus });
  };

  // Cookie consent logic
  const { categories } = useCookieConsent();
  const conversationIdRef = useRef<string>(conversationId);

  useEffect(() => {
    if (iframeLoaded) {
      const oneTrustCategoriesIds = categories.map(
        (category) => ONE_TRUST_CATEGORIES[category],
      );
      sendMessage({
        type: 'consentCategories',
        consentCategories: oneTrustCategoriesIds,
      });
    }
  }, [categories, iframeLoaded]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Handshake logic
  const trackingId = Cookies.get('optimizely_experiment_user');
  const hasAdminPermission = false;
  const connection = 'unauthenticated';
  const applicationId = 'support-centre';
  const entryPoint = 'support_centre';
  const sendHandshake = useCallback(() => {
    // Websocket Token
    sendMessage({
      type: 'handshake',
      connection,
      conversationId: conversationIdRef.current,
      locale,
      applicationId,
      entryPoint,
      trackingId,
      isFullScreen: window.innerWidth < 768,
      isMinimized,
      isMobile: window.innerWidth < 768,
      isConversationManagementEnabled: true,
      analyticsOrigin: origin,
    });
  }, [locale, trackingId, hasAdminPermission, isMinimized, conversationId]);

  // Navigation logic
  const handleNavigate = (url: string) => {
    window.open(url, '_blank');
  };

  // Conversation ID logic
  const handleConversationId = (newConversationId: string) => {
    dispatch({ type: 'SET_CONVERSATION_ID', payload: newConversationId });
  };

  // Post Message Logic (Receive)
  const parseMessage = (eventData: ChatIframeMessageEventDataType) => {
    switch (eventData.type) {
      case 'onLoad':
        sendHandshake();
        break;
      case 'onClose':
        onClose(eventData);
        break;
      case 'onMinimize':
        onMinimize(eventData.minimized);
        break;
      case 'resizeData':
        onResizeData(eventData.resizeData.width, eventData.resizeData.height);
        break;
      case 'navigateData':
        handleNavigate(eventData.navigateData.url);
        break;
      case 'conversationId':
        handleConversationId(eventData.conversationId);
        break;
      default:
        break;
    }
  };

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== iframeSrc.origin) {
      return;
    }
    // We are casting based on the contract
    // https://www.notion.so/RFC-00201-chat-sumup-com-19060cc473228070994ac0f4fd82e630
    parseMessage(event.data as ChatIframeMessageEventDataType);
  };

  useEffect(() => {
    window.addEventListener('message', onMessage);
    setIframeLoaded(true);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, []);

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeSrc.href;
    }
  }, [iFrameCounter]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <iframe
      title="Chat"
      ref={iframeRef}
      key={iFrameCounter}
      style={{
        width: size.width,
        height: size.height,
      }}
      className={clsx(
        styles.iframe,
        isAIAssistantMinimized && styles.minimized,
      )}
    />
  );
};

export default ChatIframe;
